import React, { useMemo, useState } from "react";
import formatChange from "../utils/formatChange";

export default function MarketTable({
  companies = [],
  exchangeAssets = [],
  sortConfig,
  setSortConfig,
  selectedCompany,
  setSelectedCompany,
  scope,
  userData
}) {
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpand = (itemId, e) => {
    e.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " ◢" : " ◣";
  };

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return "$0.00";
    return `$${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatMarketCap = (value) => {
    if (value == null || isNaN(value)) return "$0";
    const num = Number(value);
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const displayData = useMemo(() => {
    if (scope === "Exchange") {
      const groups = exchangeAssets.reduce((acc, curr) => {
        if (!acc[curr.itemId]) acc[curr.itemId] = [];
        acc[curr.itemId].push(curr);
        return acc;
      }, {});

      return Object.keys(groups).map(itemId => {
        const sortedListings = [...groups[itemId]].sort((a, b) => a.pricePerUnit - b.pricePerUnit);
        
        const displayName = sortedListings[0].name || 
                            itemId.replace(/_/g, " ");

        return {
          ...sortedListings[0],
          name: displayName, 
          allListings: sortedListings,
          listingCount: sortedListings.length
        };
      });
    }

    let baseList = companies.filter((c) => (c.companyStockVolume || 0) > 0);
    if (!scope || scope === "Total Market") return baseList;

    switch (scope) {
      case "Trader Companies": return baseList.filter((c) => c.companyCEO !== "NTC");
      case "Non-Trader Companies": return baseList.filter((c) => c.companyCEO === "NTC");
      case "Stocks": return baseList.filter((c) => !c.companyCrypto && !c.companyCurrency && !c.companyBenchmark);
      case "Cryptocurrency": return baseList.filter((c) => c.companyCrypto === true);
      case "Forex": return baseList.filter((c) => c.companyCurrency === true);
      case "Benchmarks": return baseList.filter((c) => c.companyBenchmark === true);
      case "Watchlist":
        if (!userData) return [];
        const ownedTickers = Object.entries(userData)
          .filter(([key, value]) => key.startsWith("company") && Number(value) >= 1)
          .map(([key]) => key.replace("company", ""));
        if (Number(userData.userGold) >= 1) ownedTickers.push("XAU");
        return baseList.filter((c) => ownedTickers.includes(c.companyTicker));
      default: return baseList;
    }
  }, [companies, exchangeAssets, scope, userData]);

  const sortedData = useMemo(() => {
    let sortable = [...displayData];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal, bVal;
        if (sortConfig.key === "MarketCap") {
          aVal = (a.companyStockPrice || 0) * (a.companyStockVolume || 0);
          bVal = (b.companyStockPrice || 0) * (b.companyStockVolume || 0);
        } else {
          aVal = a[sortConfig.key];
          bVal = b[sortConfig.key];
        }
        
        if (typeof aVal === "string") aVal = aVal.toUpperCase();
        if (typeof bVal === "string") bVal = bVal.toUpperCase();
        
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [displayData, sortConfig]);

  const columns = scope === "Exchange" 
    ? [
        { label: "ASSET", key: "name" }, 
        { label: "BEST PRICE", key: "pricePerUnit" },
        { label: "QTY", key: "quantity" },
        { label: "LISTINGS", key: "listingCount" },
        { label: "BEST SELLER", key: "sellerDisplayName" },
      ]
    : [
        { label: "TICKER", key: "companyTicker" },
        { label: "PRICE", key: "companyStockPrice" },
        { label: "AVAIL", key: "companyOutstanding" },
        { label: "MKTCAP", key: "MarketCap" },
        { label: "4HR", key: "company4Hr" },
        { label: "24HR", key: "company24Hr" },
        { label: "7D", key: "company7D" },
        { label: "30D", key: "company30D" }
      ];

  return (
    <div 
      style={{ 
        width: "100%", 
        maxHeight: "600px",
        overflowY: "auto",
        overflowX: "auto",
        position: "relative",
        border: "1px solid rgba(80, 227, 194, 0.1)",
        scrollbarWidth: "thin",
        scrollbarColor: "#50e3c2 rgba(0,0,0,0.2)"
      }}
    >
      <table 
        style={{ 
          width: "100%", 
          borderCollapse: "separate", 
          borderSpacing: "0 2px",
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "0.75rem",
        }}
      >
        <thead style={{ position: "sticky", top: 0, zIndex: 10, background: "#051a16" }}>
          <tr style={{ color: "#50e3c2", textAlign: "left" }}>
            {columns.map((col) => (
              <th 
                key={col.key}
                onClick={() => col.key && requestSort(col.key)} 
                style={{ 
                  cursor: "pointer", 
                  padding: "12px 8px", 
                  borderBottom: "1px solid #000",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  background: "#051a16", 
                }}
              >
                {col.label}{getSortArrow(col.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, idx) => {
            const isSelected = scope === "Exchange" 
                ? selectedCompany?.listingId === item.listingId 
                : selectedCompany?.id === item.id;

            if (scope === "Exchange") {
              const isExpanded = expandedItems[item.itemId];

              return (
                <React.Fragment key={item.itemId || idx}>
                  <tr 
                    onClick={() => setSelectedCompany(item)}
                    className="market-row"
                    style={{ 
                      cursor: "pointer",
                      color: isSelected ? "#50e3c2" : "#eee", 
                      background: isSelected ? "rgba(80, 227, 194, 0.15)" : "transparent",
                    }}
                  >
                    <td style={{ padding: "10px 8px", fontWeight: "bold", color: "#50e3c2" }}>
                        {item.name}
                    </td>
                    <td style={{ padding: "10px 8px" }}>{formatCurrency(item.pricePerUnit)}</td>
                    <td style={{ padding: "10px 8px", color: "#888" }}>{item.quantity}</td>
                    <td 
                        style={{ padding: "10px 8px", color: "#50e3c2", textDecoration: "underline" }}
                        onClick={(e) => toggleExpand(item.itemId, e)}
                    >
                        {item.listingCount} SELLERS {isExpanded ? "▲" : "▼"}
                    </td>
                    <td style={{ padding: "10px 8px" }}>{item.sellerDisplayName}</td>
                  </tr>

                  {isExpanded && item.allListings.slice(1).map((listing) => (
                    <tr 
                      key={listing.listingId}
                      onClick={() => setSelectedCompany(listing)}
                      style={{ 
                        background: "rgba(255, 255, 255, 0.03)", 
                        fontSize: "0.7rem", 
                        borderLeft: "2px solid #50e3c2" 
                      }}
                    >
                      <td style={{ padding: "6px 24px", color: "#666" }}>{item.name}</td>
                      <td style={{ padding: "6px 8px" }}>{formatCurrency(listing.pricePerUnit)}</td>
                      <td style={{ padding: "6px 8px", color: "#888" }}>{listing.quantity}</td>
                      <td style={{ padding: "6px 8px", color: "#555" }}>---</td>
                      <td style={{ padding: "6px 8px", color: "#aaa" }}>{listing.sellerDisplayName}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            }

            return (
              <tr
                key={item.id}
                onClick={() => setSelectedCompany(item)}
                className="market-row"
                style={{
                  cursor: "pointer",
                  background: isSelected ? "rgba(80, 227, 194, 0.15)" : "transparent",
                  color: isSelected ? "#50e3c2" : "#eee",
                }}
              >
                <td style={{ padding: "8px", fontWeight: "bold" }}>{item.companyTicker}</td>
                <td style={{ padding: "8px" }}>{formatCurrency(item.companyStockPrice)}</td>
                <td style={{ padding: "8px", color: "#888" }}>{Math.floor(Number(item.companyOutstanding)).toLocaleString()}</td>
                <td style={{ padding: "8px" }}>{formatMarketCap(item.companyStockPrice * (item.companyStockVolume || 0))}</td>
                <td style={{ padding: "8px" }}>{formatChange(item.company4Hr ?? 0)}</td>
                <td style={{ padding: "8px" }}>{formatChange(item.company24Hr ?? 0)}</td>
                <td style={{ padding: "8px" }}>{formatChange(item.company7D ?? 0)}</td>
                <td style={{ padding: "8px" }}>{formatChange(item.company30D ?? 0)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}